// ============================================================================
// RemindersTab — onglet RAPPELS de l'ecran Admin (AD-G : 1er mailer ACS)
// ============================================================================
// Porte du PoC reminders-section + test-send-card SANS les blocs « Horaire
// d'envoi recurrent » / « Envois programmes » (item 9, dispatcher absent) ni
// « Derniers envois » / « Statistiques d'envoi » (source Firestore inexistante
// en C-native -> EmptyState honnete, PDR-ADM-03).
//
// ETAT REACT (P-ADM-14) : PAS les <form action=serverAction> du PoC (Next). Les
// mutations passent par useAdminReminders + fetch, et invalident ['gest','admin'].
// Tokens du parc (regle #28, zero shadcn). i18n FR+EN.
//
// KILL-SWITCH : quand paused, les cartes d'envoi (passe/renvoi/test) sont grisees
// ET le serveur re-verifie (paused => zero envoi) — la garde UI n'est PAS la
// protection, juste le confort.

import { useState } from 'react';
import { Loader2, Mail, MailX, Send, History, Save } from 'lucide-react';
import { EmptyState, GsSegmented, GsSelect, GsToggle, QueryError, SkeletonCard, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { previousMonth, formatPeriodLong } from '../../lib/pulse/period';
import {
  useUpsertReminderContent, useSetRemindersPaused, useRunMonthlyPass,
  useResendReminder, useSendReminderTest, adminReminderErrorCode,
  REMINDER_SURVEY_TYPES,
  type AdminRemindersResponse, type ReminderContent, type ReminderContentPayload,
  type ReminderSurveyType, type MonthlyPassResult, type ResendResult, type TestSendResult,
} from '../../hooks/useAdminReminders';
import { SectionHeader } from './SectionHeader';

const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';

function toPayload(c: ReminderContent): ReminderContentPayload {
  return { subject: c.subject, heading: c.heading, ctaLabel: c.ctaLabel, intro: c.intro, footnote: c.footnote };
}

// ─── Composant principal ─────────────────────────────────────────────────────

export function RemindersTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminRemindersResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.reminders.loadError')} onRetry={onRetry} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.reminders.title')}
        description={t('gest:admin.reminders.description')}
      />
      <KillSwitchCard paused={data.paused} />
      <ContentEditorCard contents={data.contents} />
      <MonthlyPassCard paused={data.paused} />
      <ResendCard paused={data.paused} teams={data.teams} />
      <TestSendCard paused={data.paused} />
      <HistoryCard />
    </div>
  );
}

// ─── Kill-switch ──────────────────────────────────────────────────────────────

function KillSwitchCard({ paused }: { paused: boolean }) {
  const { t } = useTranslation();
  const setPaused = useSetRemindersPaused();
  return (
    <section className={`${GS_COMPONENTS.card} flex flex-wrap items-center justify-between gap-3 p-5`}>
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            paused
              ? 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400'
              : 'bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400'
          }`}
        >
          {paused ? <MailX size={18} aria-hidden="true" /> : <Mail size={18} aria-hidden="true" />}
        </span>
        <div className="min-w-0">
          <p className="text-field font-medium text-slate-800 dark:text-slate-100">
            {t('gest:admin.reminders.killSwitch.title')}
          </p>
          <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">
            {paused
              ? t('gest:admin.reminders.killSwitch.pausedHint')
              : t('gest:admin.reminders.killSwitch.activeHint')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-chip text-slate-500 dark:text-slate-400">
          {paused ? t('gest:admin.reminders.killSwitch.paused') : t('gest:admin.reminders.killSwitch.active')}
        </span>
        <GsToggle
          checked={!paused}
          disabled={setPaused.isPending}
          onCheckedChange={(next) => setPaused.mutate(!next)}
          aria-label={t('gest:admin.reminders.killSwitch.title')}
        />
      </div>
    </section>
  );
}

// ─── Editeur de contenu (par survey_type) ─────────────────────────────────────

function ContentEditorCard({ contents }: { contents: Record<ReminderSurveyType, ReminderContent> }) {
  const { t } = useTranslation();
  const upsert = useUpsertReminderContent();
  const [survey, setSurvey] = useState<ReminderSurveyType>('member_pulse');
  const [draft, setDraft] = useState<ReminderContentPayload>(() => toPayload(contents.member_pulse));

  // Adoption PENDANT LE RENDU (pattern React officiel, comme QuestionsTab) : la
  // copie se re-seed quand on change de survey_type OU quand le contenu serveur
  // change (apres notre propre sauvegarde). Les autres mutations (passe/renvoi/
  // test) ne touchent PAS le contenu -> aucune adoption intempestive.
  const serverContent = contents[survey];
  const serverKey = `${survey}|${JSON.stringify(toPayload(serverContent))}`;
  const [seededKey, setSeededKey] = useState(serverKey);
  if (serverKey !== seededKey) {
    setSeededKey(serverKey);
    setDraft(toPayload(serverContent));
  }

  const set = (patch: Partial<ReminderContentPayload>) => setDraft((cur) => ({ ...cur, ...patch }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(toPayload(serverContent));
  const valid =
    draft.subject.trim() && draft.heading.trim() && draft.ctaLabel.trim()
    && draft.intro.trim() && draft.footnote.trim();
  const errorCode = upsert.isError ? adminReminderErrorCode(upsert.error) : null;

  function save() {
    if (!dirty || !valid) return;
    upsert.mutate({
      surveyType: survey,
      payload: {
        subject: draft.subject.trim(),
        heading: draft.heading.trim(),
        ctaLabel: draft.ctaLabel.trim(),
        intro: draft.intro.trim(),
        footnote: draft.footnote.trim(),
      },
    });
  }

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
      <SectionHeader
        title={t('gest:admin.reminders.content.title')}
        description={t('gest:admin.reminders.content.description')}
      >
        <GsSegmented
          value={survey}
          onChange={(v) => setSurvey(v as ReminderSurveyType)}
          ariaLabel={t('gest:admin.reminders.content.surveyLabel')}
          options={REMINDER_SURVEY_TYPES.map((s) => ({
            value: s,
            label: t(`gest:admin.reminders.surveyTypes.${s}`),
          }))}
        />
      </SectionHeader>

      {serverContent.isDefault ? (
        <p className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.reminders.content.isDefault')}
        </p>
      ) : null}

      <div className="flex flex-col gap-3">
        <Field label={t('gest:admin.reminders.content.subject')}>
          <input
            maxLength={300}
            value={draft.subject}
            aria-label={t('gest:admin.reminders.content.subject')}
            onChange={(e) => set({ subject: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </Field>
        <Field label={t('gest:admin.reminders.content.heading')}>
          <input
            maxLength={300}
            value={draft.heading}
            aria-label={t('gest:admin.reminders.content.heading')}
            onChange={(e) => set({ heading: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </Field>
        <Field label={t('gest:admin.reminders.content.ctaLabel')}>
          <input
            maxLength={300}
            value={draft.ctaLabel}
            aria-label={t('gest:admin.reminders.content.ctaLabel')}
            onChange={(e) => set({ ctaLabel: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </Field>
        <Field label={t('gest:admin.reminders.content.intro')}>
          <textarea
            rows={3}
            maxLength={2000}
            value={draft.intro}
            aria-label={t('gest:admin.reminders.content.intro')}
            onChange={(e) => set({ intro: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </Field>
        <Field label={t('gest:admin.reminders.content.footnote')}>
          <textarea
            rows={2}
            maxLength={2000}
            value={draft.footnote}
            aria-label={t('gest:admin.reminders.content.footnote')}
            onChange={(e) => set({ footnote: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </Field>
      </div>

      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.reminders.errors.${errorCode}`)}
        </p>
      ) : null}

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!dirty || !valid || upsert.isPending}
          onClick={save}
          className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
        >
          {upsert.isPending ? (
            <Loader2 size={14} aria-hidden="true" className="animate-spin" />
          ) : (
            <Save size={14} aria-hidden="true" />
          )}
          {t('gest:admin.reminders.content.save')}
        </button>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

// ─── Passe mensuelle ──────────────────────────────────────────────────────────

function MonthlyPassCard({ paused }: { paused: boolean }) {
  const { t } = useTranslation();
  const run = useRunMonthlyPass();
  const period = previousMonth();
  const errorCode = run.isError ? adminReminderErrorCode(run.error) : null;

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
      <SectionHeader
        title={t('gest:admin.reminders.pass.title')}
        description={t('gest:admin.reminders.pass.description', { period: formatPeriodLong(period) })}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={paused || run.isPending}
          onClick={() => run.mutate({})}
          className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
        >
          {run.isPending ? (
            <Loader2 size={14} aria-hidden="true" className="animate-spin" />
          ) : (
            <Send size={14} aria-hidden="true" />
          )}
          {t('gest:admin.reminders.pass.send')}
        </button>
        {paused ? (
          <span className="text-chip text-slate-400 dark:text-slate-500">
            {t('gest:admin.reminders.pausedNote')}
          </span>
        ) : null}
      </div>
      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.reminders.errors.${errorCode}`)}
        </p>
      ) : null}
      {run.isSuccess ? <PassResultAlert result={run.data} /> : null}
    </section>
  );
}

// ─── Renvoi cible a une equipe ────────────────────────────────────────────────

function ResendCard({ paused, teams }: { paused: boolean; teams: AdminRemindersResponse['teams'] }) {
  const { t } = useTranslation();
  const resend = useResendReminder();
  const [teamId, setTeamId] = useState('');
  const [period, setPeriod] = useState(previousMonth());
  const errorCode = resend.isError ? adminReminderErrorCode(resend.error) : null;

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
      <SectionHeader
        title={t('gest:admin.reminders.resend.title')}
        description={t('gest:admin.reminders.resend.description')}
      />
      {teams.length === 0 ? (
        <p className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.reminders.resend.noTeams')}
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-52 flex-col gap-1.5">
            <label htmlFor="resend-team" className={labelCls}>
              {t('gest:admin.reminders.resend.team')}
            </label>
            <GsSelect
              value={teamId}
              onChange={setTeamId}
              ariaLabel={t('gest:admin.reminders.resend.team')}
              placeholder={t('gest:admin.reminders.resend.teamPlaceholder')}
              options={teams.map((team) => ({ value: team.id, label: team.name }))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="resend-period" className={labelCls}>
              {t('gest:admin.reminders.period')}
            </label>
            <input
              id="resend-period"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className={GS_COMPONENTS.input}
            />
          </div>
          <button
            type="button"
            disabled={paused || !teamId || !period || resend.isPending}
            onClick={() => resend.mutate({ teamId, period })}
            className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
          >
            {resend.isPending ? (
              <Loader2 size={14} aria-hidden="true" className="animate-spin" />
            ) : (
              <Send size={14} aria-hidden="true" />
            )}
            {t('gest:admin.reminders.resend.send')}
          </button>
        </div>
      )}
      {paused ? (
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.reminders.pausedNote')}
        </span>
      ) : null}
      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.reminders.errors.${errorCode}`)}
        </p>
      ) : null}
      {resend.isSuccess ? <ResendResultAlert result={resend.data} /> : null}
    </section>
  );
}

// ─── Envoi test ───────────────────────────────────────────────────────────────

function TestSendCard({ paused }: { paused: boolean }) {
  const { t } = useTranslation();
  const test = useSendReminderTest();
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [period, setPeriod] = useState(previousMonth());
  const [survey, setSurvey] = useState<ReminderSurveyType>('member_pulse');
  const errorCode = test.isError ? adminReminderErrorCode(test.error) : null;

  const recipients = recipientsRaw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
      <SectionHeader
        title={t('gest:admin.reminders.test.title')}
        description={t('gest:admin.reminders.test.description')}
      >
        <GsSegmented
          value={survey}
          onChange={(v) => setSurvey(v as ReminderSurveyType)}
          ariaLabel={t('gest:admin.reminders.content.surveyLabel')}
          options={REMINDER_SURVEY_TYPES.map((s) => ({
            value: s,
            label: t(`gest:admin.reminders.surveyTypes.${s}`),
          }))}
        />
      </SectionHeader>
      <div className="flex flex-col gap-3">
        <Field label={t('gest:admin.reminders.test.recipients')}>
          <input
            value={recipientsRaw}
            placeholder={t('gest:admin.reminders.test.recipientsPlaceholder')}
            aria-label={t('gest:admin.reminders.test.recipients')}
            onChange={(e) => setRecipientsRaw(e.target.value)}
            className={GS_COMPONENTS.input}
          />
        </Field>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="test-period" className={labelCls}>
              {t('gest:admin.reminders.period')}
            </label>
            <input
              id="test-period"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className={GS_COMPONENTS.input}
            />
          </div>
          <button
            type="button"
            disabled={paused || recipients.length === 0 || !period || test.isPending}
            onClick={() => test.mutate({ recipients, period, surveyType: survey })}
            className={`${GS_COMPONENTS.btnAction} ml-auto inline-flex touch-manipulation items-center gap-1.5`}
          >
            {test.isPending ? (
              <Loader2 size={14} aria-hidden="true" className="animate-spin" />
            ) : (
              <Send size={14} aria-hidden="true" />
            )}
            {t('gest:admin.reminders.test.send')}
          </button>
        </div>
      </div>
      {paused ? (
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.reminders.pausedNote')}
        </span>
      ) : null}
      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.reminders.errors.${errorCode}`)}
        </p>
      ) : null}
      {test.isSuccess ? <TestResultAlert result={test.data} /> : null}
    </section>
  );
}

// ─── Alertes de resultat ──────────────────────────────────────────────────────

function AlertBox({ tone, children }: { tone: 'ok' | 'warn'; children: React.ReactNode }) {
  const cls =
    tone === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300'
      : 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-900 dark:bg-brand-950 dark:text-brand-300';
  return (
    <div role="status" className={`rounded-xl border p-3 text-field ${cls}`}>
      {children}
    </div>
  );
}

function PassResultAlert({ result }: { result: MonthlyPassResult }) {
  const { t } = useTranslation();
  if (result.paused) return <AlertBox tone="warn">{t('gest:admin.reminders.result.paused')}</AlertBox>;
  if (result.alreadyRan) {
    return <AlertBox tone="warn">{t('gest:admin.reminders.result.alreadyRan', { period: result.period })}</AlertBox>;
  }
  return (
    <AlertBox tone="ok">
      <p>{t('gest:admin.reminders.result.sent', { n: result.sent })}</p>
      {result.errors > 0 ? (
        <p className="mt-1">
          {t('gest:admin.reminders.result.errors', { n: result.errors })}
          {result.throttled > 0 ? ` — ${t('gest:admin.reminders.result.throttled', { n: result.throttled })}` : ''}
        </p>
      ) : null}
      {result.skipped > 0 ? (
        <p className="mt-1">{t('gest:admin.reminders.result.skipped', { n: result.skipped })}</p>
      ) : null}
    </AlertBox>
  );
}

function ResendResultAlert({ result }: { result: ResendResult }) {
  const { t } = useTranslation();
  if (result.paused) return <AlertBox tone="warn">{t('gest:admin.reminders.result.paused')}</AlertBox>;
  return (
    <AlertBox tone="ok">
      <p>{t('gest:admin.reminders.result.sent', { n: result.sent })}</p>
      {result.errors > 0 ? (
        <p className="mt-1">{t('gest:admin.reminders.result.errors', { n: result.errors })}</p>
      ) : null}
    </AlertBox>
  );
}

function TestResultAlert({ result }: { result: TestSendResult }) {
  const { t } = useTranslation();
  if (result.paused) return <AlertBox tone="warn">{t('gest:admin.reminders.result.paused')}</AlertBox>;
  return (
    <AlertBox tone="ok">
      <p>{t('gest:admin.reminders.result.sent', { n: result.sent })}</p>
      {result.errors > 0 ? (
        <p className="mt-1">{t('gest:admin.reminders.result.errors', { n: result.errors })}</p>
      ) : null}
    </AlertBox>
  );
}

// ─── Historique (EmptyState honnete — PDR-ADM-03) ─────────────────────────────

function HistoryCard() {
  const { t } = useTranslation();
  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
      <SectionHeader
        title={t('gest:admin.reminders.history.title')}
        description={t('gest:admin.reminders.history.description')}
      />
      <EmptyState
        icon={<History size={48} aria-hidden="true" />}
        title={t('gest:admin.reminders.history.empty')}
        description={t('gest:admin.reminders.history.emptyHint')}
      />
    </section>
  );
}
